import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckoutPlan, loadOnboardingEmail, RegistrationPayload, RegistrationService, saveOnboardingEmail, TenantInfo } from '../registration.service';
import { environment } from '../../environments/environment';
import { applyRegistrationValidationError } from '../registration-validation';

type AtlasStep = 'tenantInfo' | 'subscription';

interface Status {
  loading: boolean;
}

@Component({
  selector: 'app-atlas-signup',
  templateUrl: './atlas-signup.component.html',
  styleUrls: ['./atlas-signup.component.css'],
  standalone: false,
})
export class AtlasSignupComponent implements OnInit {
  public currentStep: AtlasStep = 'tenantInfo';
  public status: Status = { loading: false };
  public userForm: FormGroup;
  public selectedPlan: CheckoutPlan | null = null;
  public registeredTenantId: string | null = null;
  public plans: CheckoutPlan[] = [];
  public plansLoading = false;
  public plansError: string | null = null;
  public subscriptionBlocked = false;
  public subscriptionBlockMessage: string | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly snackbar: MatSnackBar,
    private readonly registrationService: RegistrationService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.userForm = this.formBuilder.group({
      tenantInfo: null,
    });
  }

  ngOnInit(): void {
    const tenantInfo = this.navigationTenantInfo();
    if (tenantInfo) {
      this.userForm.patchValue({ tenantInfo });
      saveOnboardingEmail(tenantInfo.tenantName, tenantInfo.emailAddress);
    }

    const step = this.route.snapshot.queryParamMap.get('step');
    const tenantId =
      this.route.snapshot.queryParamMap.get('tenantId')
      ?? this.route.snapshot.queryParamMap.get('tenant_id');

    if (tenantId) {
      this.registeredTenantId = tenantId;
      this.currentStep = 'subscription';
    } else if (step === 'subscription') {
      this.snackbar.open('Tenant id is missing. Please complete tenant info first.', 'OK');
    }

    this.loadPlans();
  }

  loadPlans(): void {
    this.plansLoading = true;
    this.plansError = null;
    this.registrationService.getCheckoutPlans().subscribe({
      next: (response) => {
        this.plans = response?.plans || [];
        this.plansLoading = false;
      },
      error: () => {
        this.plansLoading = false;
        this.plansError = 'Unable to load plans right now.';
      },
    });
  }

  subformInitialized(name: string, group: FormGroup): void {
    this.userForm.setControl(name, group);
    this.cdr.detectChanges();
  }

  submitRegistration(): void {
    const tenantInfo = this.userForm.get('tenantInfo')?.value;
    if (!tenantInfo) {
      this.snackbar.open('Please complete tenant information first.', 'OK');
      return;
    }

    this.status.loading = true;
    const payload: RegistrationPayload = {
      tenantInfo,
      registrationInfo: {
        provider: 'datastorio_saas',
        provider_interface_id: environment.env,
      },
    };

    this.registrationService
      .register(payload)
      .subscribe({
        next: (response) => {
          this.status.loading = false;
          this.snackbar.open('Check your email to continue.', 'OK');
          const registeredTenantId = response?.tenant_name || tenantInfo.tenantName;
          saveOnboardingEmail(registeredTenantId, tenantInfo.emailAddress);
          this.router.navigate(['/thanks'], {
            queryParams: { stage: 'verify-email', tenantId: registeredTenantId },
            state: { tenantInfo: { ...tenantInfo, tenantName: registeredTenantId } },
          });
        },
        error: (err) => {
          this.status.loading = false;
          const reason = applyRegistrationValidationError(this.userForm, err);
          this.snackbar.open(`Registration failed: ${reason}`, 'OK');
        },
      });
  }

  selectPlan(plan: CheckoutPlan): void {
    this.selectedPlan = plan;
  }

  canContinueToCheckout(): boolean {
    return Boolean(
      this.selectedPlan?.subscriptionLevel &&
      this.registeredTenantId &&
      !this.status.loading &&
      !this.subscriptionBlocked
    );
  }

  continueToCheckout(): void {
    if (!this.selectedPlan?.subscriptionLevel || !this.registeredTenantId) {
      this.snackbar.open('Select a subscription level to continue.', 'OK');
      return;
    }

    const payload = {
      subscriptionLevel: this.selectedPlan.subscriptionLevel,
      priceId: this.selectedPlan.priceId || undefined,
      tenantName: this.registeredTenantId,
      tenantInfo: this.checkoutTenantInfo(),
    };

    this.status.loading = true;
    this.registrationService.createCheckoutSession(this.registeredTenantId, payload).subscribe({
      next: (response) => {
        const checkoutUrl = response?.checkout_url;
        if (!checkoutUrl) {
          this.status.loading = false;
          this.snackbar.open('Checkout session was created without a redirect URL.', 'OK');
          return;
        }

        window.location.assign(checkoutUrl);
      },
      error: (err) => {
        this.status.loading = false;
        const reason = err?.error?.reason || 'Unable to start checkout right now.';
        if (err?.status === 409) {
          this.subscriptionBlocked = true;
          this.subscriptionBlockMessage = reason;
        }
        this.snackbar.open(`Checkout failed: ${reason}`, 'OK');
      },
    });
  }

  formatPlanPrice(plan: CheckoutPlan): string {
    if (plan.unitAmount === null || plan.unitAmount === undefined) {
      return 'Custom';
    }

    const currency = (plan.currency || 'USD').toUpperCase();
    const amount = plan.unitAmount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  }

  planInterval(plan: CheckoutPlan): string {
    return plan.interval || 'month';
  }

  private checkoutTenantInfo(): TenantInfo | undefined {
    const currentTenantInfo = this.userForm.get('tenantInfo')?.value as TenantInfo | null;
    if (!currentTenantInfo?.emailAddress?.trim()) {
      return undefined;
    }

    saveOnboardingEmail(this.registeredTenantId || currentTenantInfo.tenantName, currentTenantInfo.emailAddress);
    return currentTenantInfo;
  }

  private navigationTenantInfo(): TenantInfo | undefined {
    const state = this.router.getCurrentNavigation()?.extras.state ?? history.state;
    const tenantInfo = state?.['tenantInfo'];
    if (tenantInfo && typeof tenantInfo === 'object') {
      return tenantInfo as TenantInfo;
    }

    const tenantName =
      this.route.snapshot.queryParamMap.get('tenantId')
      ?? this.route.snapshot.queryParamMap.get('tenant_id')
      ?? undefined;
    const emailAddress = loadOnboardingEmail(tenantName) ?? this.route.snapshot.queryParamMap.get('email')?.trim();
    if (!emailAddress) {
      return undefined;
    }

    return {
      ...(tenantName ? { tenantName } : {}),
      emailAddress,
    };
  }
}
